import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { usePeriod } from '../lib/period';
import { Card, ErrorState, LoadingBlock, PageHeader } from '../components/ui';
import { AiPanel, aiStateKey, PeriodLine, Section, SnapshotStatus, TrustBadge } from '../components/kit';
import { hrefWith, periodQueryOf, useDay } from '../components/a-common';
import { FinanceTrust, rowsFromTrends, type FinanceTrends } from '../components/a-finance';
import { MonthTrend } from '../components/a-charts';
import { latestDailyBriefing, type InsightsList } from '../components/a-ai';
import { evidenceHref, HealthCard, OverviewKpis, SignalsCard, UrgentSection, WorkSummaryCard, type OverviewResponse } from '../components/a-overview';

/**
 * The Executive Overview (CR-008 §16.2): one period's picture — Finance's own figures, the work against
 * the period before, people's coverage, delivery, company health, the risk rules and what needs attention
 * now — with AI's briefing beside it, labelled and optional. Read-only; every card opens its page.
 */
export function OverviewPage() {
  const { t } = useI18n();
  const { params, query } = usePeriod();
  const day = useDay();

  const overview = useQuery({
    queryKey: ['executive', 'overview', params],
    queryFn: () => api.get<OverviewResponse>('/executive/overview', { ...params }),
  });
  const trends = useQuery({
    queryKey: ['executive', 'finance', 'trends', params],
    queryFn: () => api.get<FinanceTrends>('/executive/finance/trends', { ...params }),
  });
  // The period's own briefing when its report has one; otherwise the latest day's, said as such.
  const ownBriefing = !!overview.data?.ai?.narrative;
  const insights = useQuery({
    queryKey: ['executive', 'insights', 1],
    queryFn: () => api.get<InsightsList>('/executive/insights', { page: 1 }),
    enabled: overview.isSuccess && !ownBriefing,
  });

  const data = overview.data;
  const latest = !ownBriefing ? latestDailyBriefing(insights.data?.items) : null;

  return (
    <>
      <PageHeader
        title={t('overview.title')}
        subtitle={t('overview.subtitle')}
        action={
          data?.snapshot ? (
            <Link to={hrefWith(`/reports/${data.snapshot.id}`, query)} className="inline-flex items-center gap-2 rounded-md border hairline px-3 py-2 text-body-sm text-[var(--text-secondary)] hover:border-primary/60 hover:text-primary">
              <FileText className="h-4 w-4" aria-hidden />
              {t('overview.snapshot', { revision: data.snapshot.revision })}
              <SnapshotStatus status={data.snapshot.status} />
            </Link>
          ) : undefined
        }
      />

      {overview.isLoading ? (
        <LoadingBlock />
      ) : overview.error || !data ? (
        <Card>
          <ErrorState error={overview.error ?? t('common.error')} onRetry={() => void overview.refetch()} />
        </Card>
      ) : (
        <div className="space-y-6">
          <Card>
            <div className="flex flex-col gap-2">
              <PeriodLine period={data.period} asOf={data.asOf} />
              {!data.snapshot && !data.period.live && (
                <p className="text-caption text-muted">
                  {t('overview.noSnapshot')}{' '}
                  <Link to={hrefWith('/reports', query)} className="text-primary-600 hover:underline dark:text-primary-400">
                    {t('overview.toReports')}
                  </Link>
                </p>
              )}
            </div>
            <div className="mt-3 border-t hairline pt-3">
              <FinanceTrust context={data.finance.context} />
            </div>
          </Card>

          <OverviewKpis data={data} query={query} />

          <div className="grid gap-4 xl:grid-cols-3">
            <HealthCard health={data.health} />
            <div className="space-y-2 xl:col-span-2">
              {ownBriefing || !latest?.report ? (
                <AiPanel
                  ai={data.ai}
                  title={t('overview.ai.title')}
                  evidenceLink={(id) => evidenceHref(id, query)}
                  action={
                    <Link to={hrefWith('/insights', query)} className="text-body-sm text-primary-600 hover:underline dark:text-primary-400">
                      {t('overview.ai.allInsights')}
                    </Link>
                  }
                />
              ) : (
                <>
                  <AiPanel
                    ai={latest}
                    title={t('overview.ai.latestDaily', { date: day(latest.report.periodStart) })}
                    evidenceLink={(id) => evidenceHref(id, periodQueryOf('DAILY', latest.report!.periodStart, latest.report!.periodEnd))}
                    action={
                      <Link to={hrefWith(`/reports/${latest.report.id}`, query)} className="text-body-sm text-primary-600 hover:underline dark:text-primary-400">
                        {t('overview.ai.openReport')}
                      </Link>
                    }
                  />
                  <p className="px-1 text-caption text-muted">{t('overview.ai.notThisPeriod', { reason: t(aiStateKey(data.ai?.status ?? 'NOT_REQUESTED')) })}</p>
                </>
              )}
            </div>
          </div>

          <SignalsCard risks={data.risks} positives={data.positives} currency={data.finance.context.baseCurrency} query={query} />

          <div className="grid gap-4 xl:grid-cols-2">
            <Section
              title={t('overview.trend.title')}
              subtitle={t('overview.trend.subtitle', { currency: trends.data?.context.baseCurrency ?? data.finance.context.baseCurrency })}
              action={trends.data ? <TrustBadge status={trends.data.context.status} /> : undefined}
            >
              {trends.isLoading ? (
                <LoadingBlock />
              ) : trends.error || !trends.data ? (
                <ErrorState error={trends.error ?? t('common.error')} onRetry={() => void trends.refetch()} />
              ) : (
                <MonthTrend rows={rowsFromTrends(trends.data.rows)} currency={trends.data.context.baseCurrency} />
              )}
              <p className="mt-3 text-caption text-muted">
                <Link to={hrefWith('/finance', query, { tab: 'trends' })} className="text-primary-600 hover:underline dark:text-primary-400">
                  {t('overview.trend.open')}
                </Link>
              </p>
            </Section>
            <WorkSummaryCard work={data.work} previous={data.workPrevious} basis={data.period.comparison.basis} query={query} />
          </div>

          <UrgentSection urgent={data.urgent} query={query} />

          <p className="text-caption text-[var(--text-tertiary)]">
            {t('overview.footer', {
              version: data.settingsVersion,
              work: data.definitions?.work ?? '—',
              health: data.definitions?.health ?? '—',
              risk: data.definitions?.risk ?? '—',
            })}
          </p>
        </div>
      )}
    </>
  );
}
